import * as React from 'react';
import { PlusIcon } from '@phosphor-icons/react';
import { cn } from '@/utils/utils';
import { FOCUS } from '@/components/ui/recipes';
import { LoadingSpinner } from '@/common/LoadingSpinner';

interface HeroButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'title' | 'children'> {
  title: React.ReactNode;
  /** A line under the title: why it can't be used here. */
  description?: React.ReactNode;
  /** Its action is under way: a spinner takes the plus's place and it waits. */
  busy?: boolean;
}

/** Lureo's hero button: the page's one big call to action on the hero band (New Story), its gradient sweeping on hover. */
export const HeroButton = React.forwardRef<HTMLButtonElement, HeroButtonProps>(
  ({ title, description, busy = false, disabled = false, className, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      disabled={busy || disabled}
      className={cn(
        'group relative block w-full max-w-sm overflow-hidden rounded-lureo-radius-xl text-left shadow-lureo-shadow-lift ring-1 ring-inset ring-lureo-ink-strong/5 transition-all duration-lureo-duration-slower ease-lureo-easing-enter hover:ring-lureo-ink-strong/10 hover:shadow-lureo-shadow-lift-hover active:scale-lureo-hero-press-scale',
        disabled ? 'cursor-not-allowed opacity-lureo-disabled-opacity' : 'disabled:cursor-wait',
        FOCUS,
        className,
      )}
      {...props}
    >
      <div className="relative cta-fill bg-[length:200%_100%] bg-[position:0%_50%] px-5 py-4 transition-[background-position] duration-lureo-duration-slowest ease-lureo-easing-enter group-hover:bg-[position:100%_50%] sm:px-6 sm:py-5">
        {/* Its glow and hover shine, as strong as the theme's sheen */}
        <span aria-hidden className="pointer-events-none absolute inset-0 opacity-lureo-sheen">
          <span className="absolute -top-16 left-1/2 h-36 w-[36rem] -translate-x-1/2 rounded-lureo-radius-pill bg-lureo-surface/35 opacity-55 blur-3xl" />
          <span className="absolute inset-y-0 -left-1/2 w-1/2 -skew-x-12 bg-linear-to-r from-transparent via-lureo-on-emphasis/30 to-transparent opacity-0 transition-all duration-lureo-duration-slowest ease-lureo-easing-enter group-hover:left-full group-hover:opacity-100" />
        </span>
        <div className="relative flex items-center justify-between gap-5">
          <div className="min-w-0">
            <span className="block text-lureo-24 font-bold leading-none tracking-tight text-lureo-on-cta sm:text-lureo-30 sm:leading-9">{title}</span>
            {description && <span className="mt-1.5 block text-lureo-14 font-medium text-lureo-on-cta leading-5">{description}</span>}
          </div>
          {busy ? (
            <LoadingSpinner size="xl" tone="on-cta" className="!h-11 !w-11 shrink-0 sm:!h-12 sm:!w-12" />
          ) : (
            <span
              aria-hidden
              className="relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-linear-to-br from-lureo-emphasis to-lureo-emphasis-strong text-lureo-on-emphasis shadow-lureo-shadow-control ring-1 ring-inset ring-lureo-highlight/10 transition-all duration-lureo-duration-slower ease-lureo-easing-enter sm:h-12 sm:w-12"
            >
              <PlusIcon className="h-5 w-5 transition-transform duration-lureo-duration-slower group-hover:scale-110 sm:h-[1.375rem] sm:w-[1.375rem]" />
            </span>
          )}
        </div>
      </div>
    </button>
  ),
);
HeroButton.displayName = 'HeroButton';
